import { Helmet } from "react-helmet-async";
import { useLocalization } from "../hooks/useLocalization";
import { Localization } from "../types/types";
import { buildHreflangLinks, splitLocalePath } from "../utils/localePaths";
import { buildCanonicalUrl, DEFAULT_OG_IMAGE, SITE_NAME } from "./site";

interface PageMetaProps {
  title: string;
  description: string;
  pathname: string;
  internalPath?: string;
  image?: string;
  imageAlt?: string;
  noindex?: boolean;
  type?: "website" | "article";
}

export const PageMeta = ({
  title,
  description,
  pathname,
  internalPath,
  image = DEFAULT_OG_IMAGE,
  imageAlt,
  noindex = false,
  type = "website",
}: PageMetaProps) => {
  const { locale, strings } = useLocalization();
  const canonical = buildCanonicalUrl(pathname);
  const fullTitle = title.includes(SITE_NAME) ? title : `${title} — ${SITE_NAME}`;
  const htmlLang = locale === Localization.Finnish ? "fi" : "en";
  const ogLocale = locale === Localization.Finnish ? "fi_FI" : "en_US";
  const robots = noindex ? "noindex, nofollow" : "index, follow";
  const alt = imageAlt ?? strings.SEO.OG_IMAGE_ALT;
  const hreflangPath = internalPath ?? splitLocalePath(pathname).path;
  const hreflangLinks = buildHreflangLinks(hreflangPath);

  return (
    <Helmet prioritizeSeoTags htmlAttributes={{ lang: htmlLang }}>
      <title>{fullTitle}</title>
      <meta name="description" content={description} />
      <meta name="robots" content={robots} />
      <meta name="theme-color" content="#10101d" />
      <link rel="canonical" href={canonical} />
      {hreflangLinks.map((link) => (
        <link key={link.hreflang} rel="alternate" hrefLang={link.hreflang} href={link.href} />
      ))}

      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:locale" content={ogLocale} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={canonical} />
      <meta property="og:type" content={type} />
      <meta property="og:image" content={image} />
      <meta property="og:image:alt" content={alt} />

      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={image} />
      <meta name="twitter:image:alt" content={alt} />
    </Helmet>
  );
};
